import { Button, Form, Input, message, Skeleton } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { useEffect } from "react";
import { useGetFactoryDataById } from "hooks/useFactory";
import {
  useCreateFactoryTaxInvoice,
  useUpdateFactoryTaxInvoice,
} from "hooks/useFactoryTaxInvoice";
import {
  RequestCreateFactoryTaxInvoice,
  RequestUpdateFactoryTaxInvoice,
  TaxInvoice,
} from "services/interface";
import FactoryAddress from "components/Profiles/SettingFactory/FactoryAddress";
import UploadImages from "components/UploadImages";
import { allowFileExtensionsImage } from "config";
import styled from "@emotion/styled";

interface TaxInvoiceSettingProps {
  factoryId: string;
}

function mapTaxInvoiceToAddressFac(
  ti: TaxInvoice
): {
  line_1: string;
  line_2: string;
  province_id: number;
  province_name: string;
  district_id: number;
  district_name: string;
  sub_district_id: number;
  sub_district_name: string;
  zip_code: number;
} {
  return {
    line_1: ti.line_1 ?? "",
    line_2: "",
    province_id: ti.province_id ?? 0,
    province_name: "",
    district_id: ti.district_id ?? 0,
    district_name: "",
    sub_district_id: ti.sub_district_id ?? 0,
    sub_district_name: "",
    zip_code: ti.post_code ?? 0,
  };
}

const TaxInvoiceSetting = ({ factoryId }: TaxInvoiceSettingProps) => {
  const [form] = Form.useForm();

  const { data, isLoading } = useGetFactoryDataById(factoryId);
  const createMutation = useCreateFactoryTaxInvoice();
  const updateMutation = useUpdateFactoryTaxInvoice();

  const taxInvoice = data?.factory?.tax_invoice;
  const addressFac = taxInvoice
    ? mapTaxInvoiceToAddressFac(taxInvoice)
    : undefined;

  useEffect(() => {
    if (data?.factory?.tax_invoice) {
      const ti = data.factory.tax_invoice;
      form.setFieldsValue({
        logo: ti.logo_url
          ? [{ uid: "-1", name: "logo", status: "done", url: ti.logo_url }]
          : [],
        name: ti.name,
        branch: ti.branch ?? "",
        tax_no: ti.tax_no,
        telephone: ti.telephone,
        fax: ti.fax ?? "",
        email: ti.email,
        line_1: ti.line_1,
        province_id: ti.province_id,
        district_id: ti.district_id,
        sub_district_id: ti.sub_district_id,
        zip_code: ti.post_code,
      });
    } else if (data?.factory && !data.factory.tax_invoice) {
      form.resetFields();
    }
  }, [data?.factory?.tax_invoice, data?.factory, form]);

  const logoValue = Form.useWatch("logo", form) ?? [];
  const logoFileList: UploadFile[] = Array.isArray(logoValue)
    ? logoValue.map((item: { url?: string; uid?: string }, i: number) => ({
        uid: item?.uid ?? String(i),
        name: "logo",
        status: "done" as const,
        url: item?.url,
      }))
    : [];

  if (!factoryId) {
    return (
      <span className="ant-typography ant-typography-secondary">
        ไม่พบข้อมูลโรงพยาบาล
      </span>
    );
  }

  if (isLoading) {
    return <Skeleton active />;
  }

  const onFinish = (values: Record<string, unknown>) => {
    const logoList = (values.logo as { url?: string }[]) ?? [];
    const logoUrl = logoList[0]?.url ?? "";

    const body = {
      factory_id: factoryId,
      name: String(values.name ?? ""),
      branch: String(values.branch ?? ""),
      tax_no: String(values.tax_no ?? ""),
      logo_url: logoUrl,
      telephone: String(values.telephone ?? ""),
      fax: String(values.fax ?? ""),
      email: String(values.email ?? ""),
      line_1: String(values.line_1 ?? ""),
      province_id: Number(values.province_id),
      district_id: Number(values.district_id),
      sub_district_id: Number(values.sub_district_id),
      post_code: Number(values.zip_code),
      is_active: true,
    };

    if (taxInvoice?.id) {
      const updateBody: RequestUpdateFactoryTaxInvoice = {
        ...body,
        id: taxInvoice.id,
      };
      updateMutation.mutate(updateBody, {
        onSuccess: () => {
          message.success("บันทึกข้อมูลใบกำกับภาษีสำเร็จ");
        },
        onError: () => {
          message.error("บันทึกข้อมูลใบกำกับภาษีไม่สำเร็จ");
        },
      });
    } else {
      const createBody: RequestCreateFactoryTaxInvoice = body;
      createMutation.mutate(createBody, {
        onSuccess: () => {
          message.success("บันทึกข้อมูลใบกำกับภาษีสำเร็จ");
        },
        onError: () => {
          message.error("บันทึกข้อมูลใบกำกับภาษีไม่สำเร็จ");
        },
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Form
      form={form}
      layout="horizontal"
      onFinish={onFinish}
      initialValues={{
        logo: [],
        name: "",
        branch: "",
        tax_no: "",
        telephone: "",
        fax: "",
        email: "",
      }}
      labelCol={{ span: 4 }}
      wrapperCol={{ span: 20 }}
    
    >
      <Form.Item label="โลโก้บริษัท" name="logo">
        <UploadImages
          maximumUploadItems={1}
          value={logoFileList}
          allowFileExtensions={allowFileExtensionsImage}
          onChange={(list) => {
            const next = (list ?? []).map((x: { url?: string; id?: string }) => ({
              url: x?.url,
              uid: x?.id ?? x?.url,
            }));
            form.setFieldValue("logo", next);
          }}
        />
      </Form.Item>
      <Form.Item
        label="ชื่อผู้รับใบกำกับภาษี / บริษัท"
        name="name"
        labelAlign="left"
        rules={[{ required: true, message: "กรุณากรอกชื่อ" }]}
      >
        <Input placeholder="ชื่อผู้รับใบกำกับภาษี" />
      </Form.Item>
      <Form.Item label="สาขา" name="branch" labelAlign="left">
        <Input placeholder="สาขา" />
      </Form.Item>
      <Form.Item
        label="เลขประจำตัวผู้เสียภาษี"
        name="tax_no"
        labelAlign="left"
        rules={[{ required: true, message: "กรุณากรอกเลขประจำตัวผู้เสียภาษี" }]}
      >
        <Input placeholder="เลขประจำตัวผู้เสียภาษี" />
      </Form.Item>
      <Form.Item label="โทรศัพท์" name="telephone" labelAlign="left">
        <Input placeholder="โทรศัพท์" />
      </Form.Item>
      <Form.Item label="แฟกซ์" name="fax" labelAlign="left">
        <Input placeholder="แฟกซ์" />
      </Form.Item>
      <Form.Item
        label="อีเมล"
        name="email"
        rules={[{ type: "email", message: "กรุณากรอกอีเมลให้ถูกต้อง" }]}
        labelAlign="left"
      >
        <Input placeholder="อีเมล" />
      </Form.Item>

      <FactoryAddress form={form} address_fac={addressFac} />

      <Form.Item style={{ marginTop: 16 }}>
        <WrapperButton>
          <Button
            style={{ width: 250 }}
            type="primary"
            size="large"
            htmlType="submit"
            loading={isPending}
          >
            บันทึก
          </Button>
        </WrapperButton>
      </Form.Item>
    </Form>
  );
};

export default TaxInvoiceSetting;

const WrapperButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  margin-top: 20px;
  width: 100%;
`;
